import { useEffect, useState } from "react"
import { useForm, useFieldArray, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import { Plus, Trash2, Calendar as CalendarIcon, Clock } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modules/shared/ui/components/popover"
import { Calendar } from "@/modules/shared/ui/components/calendar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { format } from "date-fns"
import { cn } from "@/lib/utils"

interface CreateExpertInterviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: {
    title: string
    notes: string
    timeSlots: { startTime: string; endTime: string }[]
  }) => void
  isSubmitting: boolean
}

const combineDateTime = (date: Date, time: string) => {
  const dateStr = format(date, "yyyy-MM-dd")
  return new Date(`${dateStr}T${time}`)
}

const timeSlotSchema = z
  .object({
    date: z.date().optional(),
    startTime: z.string(),
    endTime: z.string(),
  })
  .superRefine((slot, ctx) => {
    if (!slot.date)
      ctx.addIssue({
        code: "custom",
        path: ["date"],
        message: "Please select a date.",
      })
    if (!slot.startTime)
      ctx.addIssue({
        code: "custom",
        path: ["startTime"],
        message: "Please select a start time.",
      })
    if (!slot.endTime)
      ctx.addIssue({
        code: "custom",
        path: ["endTime"],
        message: "Please select an end time.",
      })

    if (slot.date && slot.startTime && slot.endTime) {
      const start = combineDateTime(slot.date, slot.startTime)
      const end = combineDateTime(slot.date, slot.endTime)
      if (start.getTime() >= end.getTime())
        ctx.addIssue({
          code: "custom",
          path: ["endTime"],
          message: "End time must be after start time.",
        })
      else if (start.getTime() <= Date.now())
        ctx.addIssue({
          code: "custom",
          path: ["startTime"],
          message: "Time slot must be in the future.",
        })
    }
  })

const schema = z.object({
  title: z.string().trim().min(1, "Interview title is required."),
  notes: z.string(),
  timeSlots: z.array(timeSlotSchema).min(1).max(3),
})

type FormValues = z.infer<typeof schema>

const emptySlot = { date: undefined, startTime: "", endTime: "" }
const defaultValues: FormValues = {
  title: "",
  notes: "",
  timeSlots: [emptySlot],
}

// Generate time slots in 15-minute intervals
const generateTimeOptions = () => {
  const times = []
  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 15) {
      const hour = i.toString().padStart(2, "0")
      const minute = j.toString().padStart(2, "0")
      times.push(`${hour}:${minute}`)
    }
  }
  return times
}

const timeOptions = generateTimeOptions()

type SlotValue = { date?: Date | undefined; startTime: string; endTime: string }

const getTimeOptionsForSlot = (slot: SlotValue) => {
  if (!slot.date) return timeOptions

  const now = new Date()
  const slotDate = new Date(slot.date)
  const isToday = slotDate.toDateString() === now.toDateString()

  if (!isToday) return timeOptions

  // For today, filter out past times
  const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`
  return timeOptions.filter((time) => time > currentTime)
}

const getEndTimeOptionsForSlot = (slot: SlotValue) => {
  const availableStartTimes = getTimeOptionsForSlot(slot)
  if (!slot.startTime) return availableStartTimes
  return availableStartTimes.filter((time) => time > slot.startTime)
}

export function CreateExpertInterviewDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: CreateExpertInterviewDialogProps) {
  const [calendarOpen, setCalendarOpen] = useState<{ [key: number]: boolean }>(
    {},
  )

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "timeSlots",
  })

  // Reset the form to a blank state each time the dialog is opened, so previously
  // submitted values are never retained (which made accidental duplicates likely).
  useEffect(() => {
    if (open) {
      reset(defaultValues)
      setCalendarOpen({})
    }
  }, [open, reset])

  const watchedSlots = watch("timeSlots")

  const onSubmit = (values: FormValues) => {
    const formattedSlots = values.timeSlots.map((slot) => ({
      startTime: combineDateTime(slot.date!, slot.startTime).toISOString(),
      endTime: combineDateTime(slot.date!, slot.endTime).toISOString(),
    }))

    onConfirm({
      title: values.title,
      notes: values.notes,
      timeSlots: formattedSlots,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[650px] max-h-[90vh] overflow-y-auto bg-card p-0 gap-0 border-border shadow-2xl rounded-2xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader className="bg-muted/50 px-4 sm:px-6 py-4 sm:py-5 border-b border-border">
            <DialogTitle className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
              <div className="w-1 h-4 sm:h-5 bg-primary rounded-full" />
              Schedule Expert Interview
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base text-muted-foreground mt-1.5">
              Set up an expert interview by proposing time slots for the
              candidate.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            <div className="space-y-2 sm:space-y-3">
              <Label
                htmlFor="title"
                className="text-sm font-semibold text-foreground"
              >
                Interview Title
              </Label>
              <Input
                id="title"
                placeholder="e.g. Technical Deep Dive"
                {...register("title")}
                aria-invalid={!!errors.title}
                className={cn(
                  "h-10 sm:h-11 border-border focus-visible:ring-ring transition-all text-foreground placeholder:text-muted-foreground",
                  errors.title &&
                    "border-destructive focus-visible:ring-destructive/20",
                )}
              />
              {errors.title && (
                <p className="text-xs text-destructive">
                  {errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <Label className="text-sm font-semibold text-foreground">
                  Proposed Time Slots (Max 3)
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => append(emptySlot)}
                          disabled={fields.length >= 3}
                          className="w-full sm:w-auto bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 shadow-sm transition-all h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="size-3.5 mr-1.5" /> Add Slot
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {fields.length >= 3 && (
                      <TooltipContent>
                        <p>Maximum 3 time slots allowed</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="space-y-3">
                {fields.map((field, index) => {
                  const slotValue: SlotValue =
                    watchedSlots?.[index] ?? emptySlot
                  const slotErr = errors.timeSlots?.[index]
                  return (
                    <div
                      key={field.id}
                      className="flex flex-col gap-3 p-3 sm:p-4 border border-border rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group relative"
                    >
                      <div className="grid gap-2 w-full">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <CalendarIcon className="w-3.5 h-3.5" /> Date
                        </Label>
                        <Controller
                          control={control}
                          name={`timeSlots.${index}.date`}
                          render={({ field: dateField }) => (
                            <Popover
                              open={calendarOpen[index] || false}
                              onOpenChange={(o) =>
                                setCalendarOpen((prev) => ({
                                  ...prev,
                                  [index]: o,
                                }))
                              }
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant={"outline"}
                                  className={cn(
                                    "w-full justify-start text-left font-normal bg-card border-border hover:bg-card hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                    !dateField.value && "text-muted-foreground",
                                    slotErr?.date &&
                                      "border-destructive focus:ring-destructive/20 hover:border-destructive",
                                  )}
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {dateField.value ? (
                                    format(new Date(dateField.value), "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0 bg-card"
                                align="start"
                              >
                                <Calendar
                                  mode="single"
                                  selected={
                                    dateField.value
                                      ? new Date(dateField.value)
                                      : undefined
                                  }
                                  onSelect={(date) => {
                                    dateField.onChange(date || new Date())
                                    setCalendarOpen((prev) => ({
                                      ...prev,
                                      [index]: false,
                                    }))
                                  }}
                                  disabled={{ before: new Date() }}
                                  className="rounded-md border shadow-md bg-card pointer-events-auto"
                                />
                              </PopoverContent>
                            </Popover>
                          )}
                        />
                        {slotErr?.date && (
                          <p className="text-xs text-destructive">
                            {slotErr.date.message}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full">
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          <div className="grid gap-2">
                            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" /> Start
                            </Label>
                            <Controller
                              control={control}
                              name={`timeSlots.${index}.startTime`}
                              render={({ field: startField }) => (
                                <Select
                                  value={startField.value}
                                  onValueChange={(val) => {
                                    startField.onChange(val)
                                    // Default the end time to one hour after the start.
                                    const [hours, minutes] = val
                                      .split(":")
                                      .map(Number)
                                    const endHour = (hours + 1) % 24
                                    const endTime = `${endHour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
                                    setValue(
                                      `timeSlots.${index}.endTime`,
                                      endTime,
                                      { shouldValidate: true },
                                    )
                                  }}
                                >
                                  <SelectTrigger
                                    className={cn(
                                      "bg-card border-border hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                      slotErr?.startTime &&
                                        "border-destructive focus:ring-destructive/20 hover:border-destructive",
                                    )}
                                  >
                                    <SelectValue placeholder="Start" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[200px] bg-card">
                                    {getTimeOptionsForSlot(slotValue).map(
                                      (time) => (
                                        <SelectItem key={time} value={time}>
                                          {time}
                                        </SelectItem>
                                      ),
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            {slotErr?.startTime && (
                              <p className="text-xs text-destructive">
                                {slotErr.startTime.message}
                              </p>
                            )}
                          </div>
                          <div className="grid gap-2">
                            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" /> End
                            </Label>
                            <Controller
                              control={control}
                              name={`timeSlots.${index}.endTime`}
                              render={({ field: endField }) => (
                                <Select
                                  value={endField.value}
                                  onValueChange={endField.onChange}
                                >
                                  <SelectTrigger
                                    className={cn(
                                      "bg-card border-border hover:border-primary/50 focus:ring-2 focus:ring-ring/20",
                                      slotErr?.endTime &&
                                        "border-destructive focus:ring-destructive/20 hover:border-destructive",
                                    )}
                                  >
                                    <SelectValue placeholder="End" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[200px] bg-card">
                                    {getEndTimeOptionsForSlot(slotValue).map(
                                      (time) => (
                                        <SelectItem key={time} value={time}>
                                          {time}
                                        </SelectItem>
                                      ),
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            {slotErr?.endTime && (
                              <p className="text-xs text-destructive">
                                {slotErr.endTime.message}
                              </p>
                            )}
                          </div>
                        </div>

                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors h-10 w-10 sm:mt-0"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="space-y-3">
              <Label
                htmlFor="notes"
                className="text-sm font-semibold text-foreground"
              >
                Notes for Candidate
              </Label>
              <Textarea
                id="notes"
                placeholder="Any specific instructions or topics to prepare..."
                {...register("notes")}
                className="min-h-[100px] border-border focus-visible:ring-ring resize-none bg-muted/30"
              />
            </div>
          </div>

          <DialogFooter className="bg-muted/50 px-6 py-4 border-t border-border flex gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="border-border text-foreground hover:bg-muted rounded-xl px-5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl px-6 font-medium"
            >
              {isSubmitting ? "Scheduling..." : "Schedule Interview"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
